// ─────────────────────────────────────────────────────────────────────
// BANEPAD DEVICE PAIRING — pure domain logic (proof of concept)
//
// Lets an operator associate a physical pad/tablet with ONE specific court.
// The flow has two artefacts:
//   • registrationCode — a short, human-enterable code the operator reads out
//     and types on the physical pad ("BANE-7K3Q"). Not a secret.
//   • deviceToken      — a device-scoped credential minted when pairing
//     completes. It is bound to a single court by construction, and is the
//     ONLY thing a paired device should present to identify its court.
//
// This is DEVICE pairing, NOT user authentication — a token here authorizes a
// pad to render one court's screen, nothing else. It deliberately does not use,
// and must never be confused with, the normal user/admin login.
//
// ⚠️ THIS MODULE IS THE SUPERSEDED CREDENTIAL MODEL (#663).
//
// It is NOT what a deployed pad uses. Tokens are minted server-side and stored as
// a SHA-256 hash, and the court is resolved from the device row (#184, #185,
// #182). `docs/banepad/pad-pairing.md` carries the same notice.
//
// WHAT IS LEFT HERE AFTER #663 IS THE REGISTRY SIDE ONLY — creating a pending
// registration, reading a code's expiry, resolving a stored token, revoking,
// finding a court's registration. The PAIRING half, which minted a token in the
// browser, is deleted; there is no client path that can issue a credential.
//
// The old comment asked to «issue tokens server-side, store only a HASH, and
// verify the token → court scope on the server… replace the storage boundary» —
// which is done. Removing the legacy path itself is tracked on #663.
//
//   - STILL TRUE, AND THE RULE THAT OUTLIVED THE MODEL: never authorize a pad by
//     a courtId taken from the URL or the client. Always resolve the court from
//     the verified device record. The server does exactly this, which is why
//     «cannot read another court by changing an id» is structural there — there
//     is no id to change.
//
// This module is PURE: no Storage, no React, no Date.now()/Math.random() unless
// the caller leaves the corresponding option unset (the persisted boundary and
// the UI rely on those defaults; tests inject explicit values for determinism).
// ─────────────────────────────────────────────────────────────────────

export type BanepadRegistrationStatus = 'pending' | 'paired' | 'revoked';

/**
 * How long a pending registration CODE stays usable. The code is only the
 * short-lived hand-off secret (operator → physical pad); once paired, the device
 * token it mints is long-lived (valid until revoked). Keeping the code short-lived
 * limits the window in which a leaked/overheard code could be used.
 */
export const BANEPAD_REGISTRATION_CODE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export interface BanepadDeviceRegistration {
  /** Stable registration id (used to revoke). */
  id: string;
  /** The court this device is scoped to (1-based). */
  courtId: number;
  /** Human-readable court name at registration time (display only). */
  courtName: string;
  /** Short code the operator enters on the physical pad. Not a secret. */
  registrationCode: string;
  /** Device-scoped credential, minted on pairing. null until paired / after revoke. */
  deviceToken: string | null;
  /** pending → paired → revoked. Only `paired` resolves as an active device. */
  status: BanepadRegistrationStatus;
  /** ms epoch the registration (pending code) was created. */
  createdAt: number;
  /** ms epoch the pending code expires (createdAt + TTL). The device token does NOT expire here. */
  codeExpiresAt: number;
  /** ms epoch pairing completed. */
  pairedAt?: number | null;
  /** ms epoch the device was last seen (PoC: stamped at pair/revoke). */
  lastSeenAt?: number | null;
}

// Unambiguous alphabet for the registration code — no 0/O/1/I to avoid
// read-aloud / typing mistakes on the physical pad.
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

// `normalizeCode` went with `pairWithCode` (#663): it existed to compare a typed
// code against the registry, which only the deleted browser pairing did. The
// server normalises with `normalisePairingCode` in `padPairing.ts`, and #216 is
// the reason that matters — a code stored with its dash could never be found.

/** How many characters a pairing code carries. See the comment below. */
export const REGISTRATION_CODE_LENGTH = 8;

/**
 * Generates a registration code, e.g. "7K3QM4XB".
 *
 * NO PREFIX (#216). Codes used to read "BANE-7K3Q" — four random characters
 * behind a constant word. Every code had the word, so it carried no
 * information: it cost four characters of typing and bought nothing, while the
 * random part was only 32⁴ ≈ 1 million. The same keystrokes spent on
 * randomness give 32⁸ ≈ 10¹².
 *
 * Product decision by the owner, 2026-09-20: "it is more secure to remove the
 * bane part". It also makes one format for the whole system — the tenant
 * migration already issued eight unprefixed characters, which the pad could
 * not type.
 */
export function generateRegistrationCode(opts: { random?: () => number } = {}): string {
  const rnd = opts.random ?? Math.random;
  let code = '';
  for (let i = 0; i < REGISTRATION_CODE_LENGTH; i++) {
    code += CODE_ALPHABET[Math.floor(rnd() * CODE_ALPHABET.length)];
  }
  return code;
}

export interface CreateRegistrationOpts {
  now?: number;
  id?: string;
  registrationCode?: string;
}

/** Builds a fresh `pending` registration for a court (no device token yet). */
export function createRegistrationRecord(
  courtId: number | string,
  courtName: string | null | undefined,
  opts: CreateRegistrationOpts = {},
): BanepadDeviceRegistration {
  const now = opts.now ?? Date.now();
  const cid = Number(courtId);
  return {
    id: opts.id ?? `bpdreg_${now}_${Math.random().toString(36).slice(2, 8)}`,
    courtId: cid,
    courtName: String(courtName ?? `Bane ${cid}`),
    registrationCode: opts.registrationCode ?? generateRegistrationCode(),
    deviceToken: null,
    status: 'pending',
    createdAt: now,
    codeExpiresAt: now + BANEPAD_REGISTRATION_CODE_TTL_MS,
    pairedAt: null,
    lastSeenAt: null,
  };
}

/** True when a pending registration's code has passed its TTL. Paired/revoked never count as expired. */
export function isBanepadCodeExpired(
  reg: Pick<BanepadDeviceRegistration, 'status' | 'codeExpiresAt'>,
  now: number = Date.now(),
): boolean {
  return reg.status === 'pending' && now > (reg.codeExpiresAt ?? Infinity);
}

/**
 * Why a pairing attempt was refused.
 *
 * `server_required` is NOT a user-facing outcome: it means this build is running
 * off the `http` backend, where there is no server to pair with and nothing is
 * allowed to mint a credential in its place (#663). A deployed pad cannot see it.
 */
export type PairError = 'invalid_code' | 'not_found' | 'revoked' | 'expired' | 'server_required';

export type PairResult =
  | {
      ok: true;
      registrations: BanepadDeviceRegistration[];
      registration: BanepadDeviceRegistration;
    }
  | { ok: false; error: PairError };

// ── NO `pairWithCode` HERE ANY MORE, AND THAT IS THE POINT (#663) ───
//
// It took a registration list and a code and MINTED A DEVICE TOKEN IN THE BROWSER
// (`Math.random`, ten base-36 characters) when the registration had none. Both it
// and the minter are deleted.
//
// SO NOTHING IN THE CLIENT PATH CAN MINT A DEVICE CREDENTIAL — which is the true
// statement, and narrower than «nothing in this tree can». `POST /pad/pair` in this
// same repository still mints the real one, deliberately: 256 bits server-side,
// stored only as a hash. There is now exactly ONE issuer, and it is not the
// browser.
//
// WHERE EACH RULE IT TESTED NOW LIVES, so none of them was simply dropped:
//
//   code normalisation (case, separators)   `normalisePairingCode` + «accepts the
//                                            code however it is typed off the
//                                            screen»
//   unknown / empty code refused            «answers the same for an unknown code»
//   revoked registration refused            «refuses a revoked device's old code»
//   expired code refused                    «refuses an expired code»
//
// TWO OF ITS RULES THE SERVER DELIBERATELY DOES NOT SHARE, and they are differences
// rather than gaps:
//
//   • IDEMPOTENT RE-PAIRING. This function re-paired an already-paired
//     registration and kept its token. A code is now SINGLE-USE — «the same code
//     cannot pair a second tablet» — which is the stronger rule and the intended
//     one.
//   • THE EXPIRY BOUNDARY, by one millisecond and in the opposite direction. This
//     used `now > codeExpiresAt`, so a code paired AT its expiry instant; the
//     server uses `codeExpiresAt <= now` and refuses there. Operationally
//     irrelevant for a code that lives minutes, and pinned server-side rather
//     than normalised either way — which convention is right is not an
//     implementer's call.

/**
 * Resolves a device token to its registration — the lookup a paired pad would
 * perform to learn which court it may show. Returns the registration ONLY when
 * it is `paired`; pending and revoked tokens resolve to null (no active device).
 * The returned registration carries the court scope — authorize from THIS, never
 * from a courtId supplied by the client/URL.
 */
export function resolveDeviceToken(
  list: BanepadDeviceRegistration[] | null | undefined,
  deviceToken: string | null | undefined,
): BanepadDeviceRegistration | null {
  const tok = String(deviceToken ?? '');
  if (!tok) return null;
  const reg = (list ?? []).find((r) => !!r.deviceToken && r.deviceToken === tok);
  if (!reg || reg.status !== 'paired') return null;
  return reg;
}

export interface RevokeResult {
  registrations: BanepadDeviceRegistration[];
  registration: BanepadDeviceRegistration | null;
}

/**
 * Revokes a registration by id (the demo "unpair / reset"). Clears the device
 * token and flips status to `revoked` so the old token can no longer resolve.
 * Returns a NEW list; `registration` is null if the id was unknown.
 */
export function revokeRegistration(
  list: BanepadDeviceRegistration[] | null | undefined,
  deviceId: string,
  opts: { now?: number } = {},
): RevokeResult {
  const src = list ?? [];
  const idx = src.findIndex((r) => r.id === deviceId);
  if (idx === -1) return { registrations: src, registration: null };
  const now = opts.now ?? Date.now();
  const revoked: BanepadDeviceRegistration = {
    ...src[idx],
    status: 'revoked',
    deviceToken: null,
    lastSeenAt: now,
  };
  const registrations = src.slice();
  registrations[idx] = revoked;
  return { registrations, registration: revoked };
}

/**
 * Returns the active (pending or paired) registration for a court, preferring a
 * paired one. Revoked registrations are ignored. null when the court has none.
 */
export function findRegistrationForCourt(
  list: BanepadDeviceRegistration[] | null | undefined,
  courtId: number | string,
): BanepadDeviceRegistration | null {
  const cid = Number(courtId);
  const active = (list ?? []).filter((r) => Number(r.courtId) === cid && r.status !== 'revoked');
  return active.find((r) => r.status === 'paired') ?? active[0] ?? null;
}
